import { LitElement, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { repeat } from 'lit/directives/repeat.js';
import { dispatch } from '../../utils/event-helpers';
import { generateUid } from '../../utils/uid';
import { styles } from './asc-textarea-input.styles';

type TextareaInputMode = 'default' | 'tags';

/**
 * A multi-line text input supporting free-form text entry and an optional tag/chip mode
 * where discrete values are entered and removed inline.
 *
 * The `label` property renders, alongside an optional required asterisk (`*`), directly
 * in the shadow DOM label row. Slotting a help/tooltip icon next to the label is supported
 * via `slot="help-icon"`. Hint and error copy flow through light-DOM slots below the field.
 *
 * **Default mode** wraps a native `<textarea>` (resize handle exposed by default).
 * **Tags mode** renders committed chips + a trailing `<input>`. Enter or comma commits;
 * Backspace on an empty input removes the last chip.
 *
 * @element asc-textarea-input
 *
 * @fires asc-input      - Fired on every keystroke in default mode. Detail: `{ value: string }`
 * @fires asc-change     - Fired on blur in default mode. Detail: `{ value: string }`
 * @fires asc-tag-add    - Fired when a new tag is committed (Enter / comma). Detail: `{ tag: string }`
 * @fires asc-tag-remove - Fired when a tag chip remove button is activated or Backspace on empty input. Detail: `{ tag: string }`
 * @fires asc-focus      - Fired when the component receives focus (either mode). Detail: `{}`
 * @fires asc-blur       - Fired when the component loses focus (either mode). Detail: `{}`
 *
 * @slot help-icon     - Optional. A tooltip icon button rendered after the label text and required asterisk.
 * @slot hint-text     - Optional. Helper text rendered below the field. Hidden when `destructive` is true.
 * @slot error-message - Optional. Validation error shown when `destructive` is true. Announced via `role="alert"`.
 *
 * @csspart base                - Root flex-column wrapper.
 * @csspart label-row           - Flex row: label text + required asterisk + help-icon slot.
 * @csspart label-text          - The label `<span>` — override typography and color.
 * @csspart required-indicator  - The `*` asterisk `<span>` — override color.
 * @csspart field-wrapper       - The bordered field container — override border, background, shadow, border-radius.
 * @csspart textarea            - The native `<textarea>` (default mode) — override font, resize, padding.
 * @csspart chips-area          - Chip list + inline input wrapper (tags mode) — override gap, min-height.
 * @csspart chip                - Individual tag chip — override background, border, border-radius, padding.
 * @csspart chip-label          - Chip text `<span>` — override font, color.
 * @csspart chip-remove         - Chip remove `<button>` (×) — override size, color.
 * @csspart inline-input        - Trailing `<input>` for entering new tags — override font, min-width.
 * @csspart footer              - Row below the field containing hint and error.
 * @csspart hint                - Hint text container — override color, font-size.
 * @csspart error               - Error message container — override color, font-size.
 *
 * @cssprop --asc-textarea-input-bg                             - Field background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-textarea-input-bg-disabled                    - Field background (disabled). Default: `var(--asc-bg-disabled_subtle)`
 * @cssprop --asc-textarea-input-border-color                   - Idle border color. Default: `var(--asc-border-primary)`
 * @cssprop --asc-textarea-input-border-color-focus             - Focused border color. Default: `var(--asc-border-brand)`
 * @cssprop --asc-textarea-input-border-color-destructive       - Destructive idle border. Default: `var(--asc-border-error_subtle)`
 * @cssprop --asc-textarea-input-border-color-destructive-focus - Destructive focused border. Default: `var(--asc-border-error)`
 * @cssprop --asc-textarea-input-border-color-disabled          - Disabled border. Default: `var(--asc-border-disabled)`
 * @cssprop --asc-textarea-input-border-width                   - Border width. Default: `var(--asc-border-width)`
 * @cssprop --asc-textarea-input-border-radius                  - Corner radius. Default: `var(--asc-radius-md)`
 * @cssprop --asc-textarea-input-padding-x                      - Horizontal inner padding (default mode). Default: `14px`
 * @cssprop --asc-textarea-input-padding-y                      - Vertical inner padding (both modes). Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-textarea-input-min-height                     - Minimum field height. Default: `96px`
 * @cssprop --asc-textarea-input-label-gap                      - Gap between all rows (label / field / footer). Default: `var(--asc-spacing-sm)`
 * @cssprop --asc-textarea-input-label-font-size                - Label text font size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-textarea-input-label-color                    - Label text color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-textarea-input-required-color                 - Required asterisk color. Default: `var(--asc-text-brand-tertiary)`
 * @cssprop --asc-textarea-input-text-color                     - Typed text color. Default: `var(--asc-text-primary)`
 * @cssprop --asc-textarea-input-text-color-disabled            - Text color (disabled). Default: `var(--asc-text-disabled)`
 * @cssprop --asc-textarea-input-placeholder-color              - Placeholder color. Default: `var(--asc-text-placeholder)`
 * @cssprop --asc-textarea-input-font-size                      - Field font size. Default: `var(--asc-font-size-text-md)`
 * @cssprop --asc-textarea-input-font-weight                    - Field font weight. Default: `var(--asc-font-weight-regular)`
 * @cssprop --asc-textarea-input-line-height                    - Field line height. Default: `var(--asc-line-height-text-md)`
 * @cssprop --asc-textarea-input-hint-color                     - Hint text color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-textarea-input-hint-font-size                 - Hint font size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-textarea-input-error-color                    - Error text color. Default: `var(--asc-text-error-primary)`
 * @cssprop --asc-textarea-input-error-font-size                - Error font size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-textarea-input-chip-bg                        - Chip background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-textarea-input-chip-border-color              - Chip border color. Default: `var(--asc-border-primary)`
 * @cssprop --asc-textarea-input-chip-border-radius             - Chip corner radius. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-textarea-input-chip-text-color                - Chip label color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-textarea-input-chip-font-size                 - Chip label font size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-textarea-input-chip-padding-y                 - Chip top/bottom padding. Default: `var(--asc-spacing-xxs)`
 * @cssprop --asc-textarea-input-chip-padding-start             - Chip left padding. Default: `9px`
 * @cssprop --asc-textarea-input-chip-padding-end               - Chip right padding. Default: `var(--asc-spacing-xs)`
 * @cssprop --asc-textarea-input-chip-gap                       - Gap between chips and inline input. Default: `var(--asc-spacing-md)`
 * @cssprop --asc-textarea-input-chip-remove-color              - Remove icon color. Default: `var(--asc-fg-tertiary)`
 * @cssprop --asc-textarea-input-chip-remove-color-hover        - Remove icon hover color. Default: `var(--asc-fg-primary)`
 */
@customElement('asc-textarea-input')
export class AscTextareaInput extends LitElement {
  static override styles = [styles];

  static override shadowRootOptions: ShadowRootInit = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  private readonly _uid = generateUid();

  // ─── Reflected properties ─────────────────────────────────

  /** Marks the field as required; sets `aria-required="true"` and renders the `*` asterisk. */
  @property({ type: Boolean, reflect: true }) required = false;

  /** Disables interaction and applies a dimmed visual state. Default mode only. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /**
   * Activates the error state: subtle→full error border, `aria-invalid="true"`, reveals
   * the `error-message` slot and hides the `hint-text` slot.
   */
  @property({ type: Boolean, reflect: true }) destructive = false;

  /**
   * Controls the input behaviour.
   * - `'default'` — multi-line free-form `<textarea>`.
   * - `'tags'` — chip/tag entry with an inline `<input>`.
   */
  @property({ type: String, reflect: true }) mode: TextareaInputMode = 'default';

  // ─── Non-reflected properties ──────────────────────────────

  /**
   * The visible field label rendered in `part="label-text"` above the input.
   * A required asterisk is appended automatically when `required` is true.
   */
  @property({ type: String }) label = '';

  /** Placeholder text shown inside the field when empty. */
  @property({ type: String }) placeholder = '';

  /**
   * Current string value of the textarea. Default mode only; ignored in tags mode.
   * Supports both controlled (set `value` on every `asc-input`) and uncontrolled binding.
   */
  @property({ type: String }) value = '';

  /**
   * The current set of tag chip values. Required when `mode="tags"`; ignored in default mode.
   * Never mutate in place — always reassign the full array reference.
   */
  @property({ type: Array }) tags: string[] = [];

  // ─── Internal state ────────────────────────────────────────

  /** Current text in the tags-mode inline input, not yet committed as a chip. */
  @state() private _tagInputValue = '';

  // ─── DOM references ────────────────────────────────────────

  @query('[part="inline-input"]') private _inlineInput!: HTMLInputElement;

  // ─── Default mode handlers ─────────────────────────────────

  private _handleInput(e: Event): void {
    this.value = (e.target as HTMLTextAreaElement).value;
    dispatch<{ value: string }>(this, 'asc-input', { detail: { value: this.value } });
  }

  private _handleChange(e: Event): void {
    dispatch<{ value: string }>(this, 'asc-change', {
      detail: { value: (e.target as HTMLTextAreaElement).value },
    });
  }

  // ─── Tags mode handlers ────────────────────────────────────

  private _handleTagInput(e: InputEvent): void {
    this._tagInputValue = (e.target as HTMLInputElement).value;
  }

  private _handleTagKeydown(e: KeyboardEvent): void {
    const input = e.target as HTMLInputElement;
    const value = this._tagInputValue.trim();

    if (e.key === 'Enter' || e.key === ',') {
      if (value) {
        e.preventDefault();
        dispatch<{ tag: string }>(this, 'asc-tag-add', { detail: { tag: value } });
        this._tagInputValue = '';
      } else {
        e.preventDefault(); // prevent form submit / comma insertion when empty
      }
    } else if (e.key === 'Backspace' && input.value === '') {
      if (this.tags.length > 0) {
        dispatch<{ tag: string }>(this, 'asc-tag-remove', {
          detail: { tag: this.tags[this.tags.length - 1]! },
        });
      }
    } else if (e.key === 'ArrowLeft' && input.selectionStart === 0 && input.selectionEnd === 0) {
      const removes = this._getAllChipRemoves();
      const last = removes[removes.length - 1];
      last?.focus();
    } else if (e.key === 'Escape') {
      this._tagInputValue = '';
    }
  }

  private _handleChipRemoveClick(e: MouseEvent): void {
    const btn = e.currentTarget as HTMLButtonElement;
    const index = Number(btn.dataset['index']);
    const tag = this.tags[index];
    if (tag !== undefined) {
      dispatch<{ tag: string }>(this, 'asc-tag-remove', { detail: { tag } });
    }
  }

  private _handleChipKeydown(e: KeyboardEvent): void {
    const btn = e.currentTarget as HTMLButtonElement;
    const index = Number(btn.dataset['index']);

    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      const tag = this.tags[index];
      if (tag !== undefined) {
        dispatch<{ tag: string }>(this, 'asc-tag-remove', { detail: { tag } });
      }
    } else if (e.key === 'ArrowLeft') {
      const removes = this._getAllChipRemoves();
      const prev = removes[index - 1];
      if (prev) {
        prev.focus();
      }
    } else if (e.key === 'ArrowRight') {
      const removes = this._getAllChipRemoves();
      const next = removes[index + 1];
      if (next) {
        next.focus();
      } else {
        this._inlineInput?.focus();
      }
    }
  }

  private _handleChipsAreaClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    if (!target.closest('[part="chip"]') && target !== this._inlineInput) {
      this._inlineInput?.focus();
    }
  }

  // ─── Focus / blur handlers ─────────────────────────────────

  private _handleFocus(): void {
    dispatch(this, 'asc-focus', { detail: {} });
  }

  private _handleBlur(): void {
    dispatch(this, 'asc-blur', { detail: {} });
  }

  // ─── Helpers ───────────────────────────────────────────────

  private _getAllChipRemoves(): HTMLButtonElement[] {
    return Array.from(
      this.shadowRoot?.querySelectorAll<HTMLButtonElement>('[part="chip-remove"]') ?? []
    );
  }

  private _describedBy(): string {
    return this.destructive ? `${this._uid}-error` : `${this._uid}-hint`;
  }

  // ─── Render ────────────────────────────────────────────────

  private _renderDefaultMode() {
    return html`
      <textarea
        part="textarea"
        id="${this._uid}-input"
        aria-labelledby="${this._uid}-label"
        aria-describedby="${this._describedBy()}"
        aria-required="${ifDefined(this.required ? 'true' : undefined)}"
        aria-invalid="${ifDefined(this.destructive ? 'true' : undefined)}"
        aria-disabled="${ifDefined(this.disabled ? 'true' : undefined)}"
        ?disabled=${this.disabled}
        placeholder=${ifDefined(this.placeholder || undefined)}
        .value=${live(this.value)}
        @input=${this._handleInput}
        @change=${this._handleChange}
        @focus=${this._handleFocus}
        @blur=${this._handleBlur}
      ></textarea>
    `;
  }

  private _renderTagsMode() {
    return html`
      <div
        part="chips-area"
        role="group"
        aria-labelledby="${this._uid}-label"
        aria-describedby="${this._describedBy()}"
        @click=${this._handleChipsAreaClick}
      >
        ${repeat(
          this.tags,
          (_, i) => i,
          (tag, i) => html`
            <span part="chip">
              <span part="chip-label">${tag}</span>
              <button
                part="chip-remove"
                type="button"
                aria-label="Remove ${tag}"
                data-index="${i}"
                @click=${this._handleChipRemoveClick}
                @keydown=${this._handleChipKeydown}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M2 2L10 10M10 2L2 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </span>
          `
        )}
        <input
          part="inline-input"
          type="text"
          aria-label="Add tag"
          aria-required="${ifDefined(this.required ? 'true' : undefined)}"
          aria-invalid="${ifDefined(this.destructive ? 'true' : undefined)}"
          placeholder=${ifDefined(this.placeholder || undefined)}
          .value=${live(this._tagInputValue)}
          @input=${this._handleTagInput}
          @keydown=${this._handleTagKeydown}
          @focus=${this._handleFocus}
          @blur=${this._handleBlur}
        />
      </div>
    `;
  }

  override render() {
    return html`
      <div part="base">
        <div part="label-row" id="${this._uid}-label">
          <span part="label-text">${this.label}</span>
          ${this.required
            ? html`<span part="required-indicator" aria-hidden="true">*</span>`
            : ''}
          <slot name="help-icon"></slot>
        </div>

        <div part="field-wrapper">
          ${this.mode === 'tags' ? this._renderTagsMode() : this._renderDefaultMode()}
        </div>

        <div part="footer">
          <span
            part="hint"
            id="${this._uid}-hint"
            role="note"
            ?hidden=${this.destructive}
          >
            <slot name="hint-text"></slot>
          </span>
          <span
            part="error"
            id="${this._uid}-error"
            role="alert"
            ?hidden=${!this.destructive}
          >
            <slot name="error-message"></slot>
          </span>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-textarea-input': AscTextareaInput;
  }
}
